/* eslint-disable max-lines */
import { startOfDay } from 'date-fns';
import React, {Children, useEffect, useState} from 'react';
import {
  HSDrug,
  PatchApplicationActivityDto,
  PatchApplicationActivityTypeDto,
  PatchApplicationDto,
  PatchApplicationScheduledActivityDto,
  PatchApplicationScheduledActivityStatusDto,
  PatchApplicationScheduledActivityTypeDto,
  PatchApplicationStatusDto,
} from 'server-openapi';
import styled, {useTheme} from 'styled-components';
import { DateUtils } from '../../../../core/utils/dateUtils';
import { Button } from '../../../../kit/Button';
import { Layout } from '../../../../kit/Layout';
import { Text } from '../../../../kit/Text';
import { useSyncCenter } from '../../../../syncstream/SyncCenterProvider';
import { useApiUtils } from '../../../../syncstream/utils/hooks/useApiUtils';
import {MedicationInformationProps} from './MedicationInformationBox';
import { toasts } from '../../../../kit/Toasts/Toaster';
import {DrugUtils} from "../../../../syncstream/utils/DrugUtils";
import {AdministrationPermissions} from "../MedicationListsTabbedRouter/MedicationListsTabbedRouter";
import {PatchApplicationDialog} from "../patches/PatchApplicationDialog";
import {getPatchApplicationDescription} from "../../../../syncstream/utils/PatchApplicationUtils";

interface PatchApplicationActivity {
  activityTime: string;
  activityType: "Scheduled" | "Performed";
  scheduledActivity?: PatchApplicationScheduledActivityDto;
  performedActivity?: PatchApplicationActivityDto;
}

interface Props extends MedicationInformationProps {
  drug: HSDrug;
  isLate: boolean;
  hasBeenDosed: boolean;
  canReadminister: boolean;
}

export function canManage(administrationPermissions: AdministrationPermissions, drug: HSDrug, isSighting: boolean) {
  if (administrationPermissions.canAdministerPatchMedicationIncludingS8) {
    return true;
  }
  if (isSighting) {
    return administrationPermissions.canSightPatch;
  }
  const isControlled = DrugUtils.getDrugWarnings(drug!).controlledDrug;
  if (!isControlled && administrationPermissions.canAdministerPatchMedicationExcludingS8) {
    return true;
  }
  return false;

}

//eslint-disable-next-line max-lines-per-function
export function PatchApplicationItems(props: Props) {
  const patchApplication = props.patchApplication;
  const [patchApplicationActivity, setPatchApplicationActivity] = useState<PatchApplicationActivityDto | undefined>();
  const [patchApplicationScheduledActivity, setPatchApplicationScheduledActivity] = useState<PatchApplicationScheduledActivityDto | undefined>();
  const services = useSyncCenter();
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState<string | undefined>();
  const [viewOnly, setViewOnly] = useState<boolean>(false);
  const [activityKind, setActivityKind] = useState<PatchApplicationActivityTypeDto>(PatchApplicationActivityTypeDto.Sighted);
  const [unscheduledActivity, setUnscheduledActivity] = useState<PatchApplicationActivityDto>({
    patchApplicationId: patchApplication?.hsId, 
    activityType: PatchApplicationActivityTypeDto.Sighted
  });
  if (!patchApplication) {
    return null;
  }
  
  const apiUtils = useApiUtils();
  const [activities, setActivities] = useState<PatchApplicationActivity[]>([]);
  // Get the master list of activities.  This is the union of scheduled and unscheduled.
  useEffect(() => {
    let result: PatchApplicationActivity[] = [];
    patchApplication.activities?.forEach(act => {
      const newActivity: PatchApplicationActivity = {
        activityType: "Performed",
        activityTime: act.clientCreatedAt!,
        performedActivity: act
      };
      const scheduledActivity = patchApplication.scheduledActivities?.find(x => x.currentActivityId === act.hsId || x.hsId === act.scheduledActivityId);
      if (scheduledActivity) {
        newActivity.scheduledActivity = scheduledActivity;
      }
      result.push(newActivity);
    });
    patchApplication.scheduledActivities?.forEach(act => {
      // Only include it if it is not related to a scheduled activity.
      if (!result.some(x => x.scheduledActivity?.hsId === act.hsId)) {
        result.push({
          activityType: "Scheduled",
          scheduledActivity: act,
          activityTime:act.scheduledAt!
        });
      }
    });
    setActivities(result.sort((a, b) => DateUtils.compareDateStringsDescending(b.performedActivity?.clientUpdatedAt ?? b.activityTime, a.performedActivity?.clientUpdatedAt ?? a.activityTime)));
  }, [patchApplication]);
  

  const now = new Date();

  function openActivityDialog(kind: PatchApplicationActivityTypeDto, drug: HSDrug, viewOnly: boolean, lbl?: string, activity?: PatchApplicationActivityDto, scheduledActivity?: PatchApplicationScheduledActivityDto) {
    if (!canManage(props.administrationPermissions, drug, kind === PatchApplicationActivityTypeDto.Sighted)) {
      toasts.error('You do not have permission to manage this patch');
    }
    else {
      setActivityKind(kind);
      setUnscheduledActivity({
        patchApplicationId: patchApplication?.hsId,
        activityType: kind
      });
      setPatchApplicationActivity(activity);
      setPatchApplicationScheduledActivity(scheduledActivity);
      setLabel(lbl);
      setOpen(true);
      setViewOnly(viewOnly);
    }
  }

  function CheckIfCanBeSighted(scheduledActivity?: PatchApplicationScheduledActivityDto) {
    if (!scheduledActivity) {
      return false;
    } 
    // You can always sight patches.
    let currentTime = props.currentRound?.createdAt ? DateUtils.toDate(props.currentRound.createdAt) : new Date();
    const roundWindow = apiUtils.rounds.getRoundWindow(currentTime, props.facilityGroupId);
    if (DateUtils.toDate(scheduledActivity.scheduledAt!).valueOf() < DateUtils.toDate(roundWindow.start).valueOf()) {
      return false;
    }
    if (DateUtils.toDate(scheduledActivity.scheduledAt!).valueOf() > DateUtils.toDate(roundWindow.end).valueOf()) {
      return false;
    }
    return true;
  }
  function CheckIfCanBeSightedLate(scheduledActivity?: PatchApplicationScheduledActivityDto)
  {
    if (!scheduledActivity || !props.canReadminister) {
      // Not allowed to readminister.
      return false;
    }
    return (DateUtils.toDate(scheduledActivity.scheduledAt!) >= startOfDay(new Date()));
  }
  function isFuture(scheduledActivity?: PatchApplicationScheduledActivityDto)
  {
    if (!scheduledActivity) {
      return false;
    }
    let currentTime = props.currentRound?.createdAt ? DateUtils.toDate(props.currentRound.createdAt) : new Date();
    const roundWindow = apiUtils.rounds.getRoundWindow(currentTime, props.facilityGroupId);
    return (DateUtils.toDate(scheduledActivity.scheduledAt!) > DateUtils.toDate(roundWindow.end));
  }

  function isInRound(scheduledActivity?: PatchApplicationScheduledActivityDto)
  {
    if (!scheduledActivity || !props.currentRound) {
      return false;
    }
    return props.roundSchedule?.some(x => x.patchApplicationActivity?.scheduledActivity === scheduledActivity);
  }

  function getFirstScheduleItem(activities: PatchApplicationActivity[]): PatchApplicationActivity[] {
    activities = activities.sort((a, b) => a.activityTime.localeCompare(b.activityTime));
    if (!!props.currentRound) {
      const roundActivity = activities.find(x => isInRound(x.scheduledActivity));
      if (!!roundActivity) {
        return [roundActivity];
      }
      return [];
    }
    // Get the next available activity that has not been performed.
    let result = activities.find(x => !!x.scheduledActivity && x.scheduledActivity.status === PatchApplicationScheduledActivityStatusDto.Pending && CheckIfCanBeSighted(x.scheduledActivity));
    const available_activities = activities.filter(x => !!x.performedActivity).sort((a, b) => a.activityTime.localeCompare(b.activityTime));
    
    if (!result && available_activities && available_activities.length > 0) {
      // Everything has been performed, so show the last one that was performed.
      result = available_activities[available_activities.length - 1];
    }
    if (result) {
      return [result];
    }
    return [];
  }
  // Don't display checks that are after the current window ends.
  function shouldBeDisplayed(scheduledActivity: PatchApplicationScheduledActivityDto | undefined)
  {
    if (!scheduledActivity) {
      // This is an ad-hoc activity - display it.
      return true;
    }
    if (scheduledActivity.status === PatchApplicationScheduledActivityStatusDto.Inactive) {
      return  false;
    }
    return (!isFuture(scheduledActivity) || scheduledActivity.scheduledActivityType !== PatchApplicationScheduledActivityTypeDto.Sight); 
  }
  
  function isLate(scheduledActivity?: PatchApplicationScheduledActivityDto): boolean {
    if (!scheduledActivity) {
      return false;
    }
    let currentTime = props.currentRound?.createdAt ? DateUtils.toDate(props.currentRound.createdAt) : new Date();
    const roundWindow = apiUtils.rounds.getRoundWindow(currentTime, props.facilityGroupId);
    // So it is either one that has been missed or a future dose.
    // If it is an activity for today, and is before the current time, then it must be one that can be dosed late.
    return (DateUtils.toDate(scheduledActivity.scheduledAt!) < DateUtils.toDate(roundWindow.start));
  }
  const theme = useTheme();

  const isAlreadyFinished = (patchApplication.status === PatchApplicationStatusDto.Removed ||
      patchApplication.status === PatchApplicationStatusDto.FallenOff);
  function updatedProps(original: Props, activity?: PatchApplicationScheduledActivityDto): Props {
    return {...original, isLate: isLate(activity)};
  }
  function getDescription(status: PatchApplicationStatusDto | undefined) {
    if (!status) {
      return "";
    }
    if (status === PatchApplicationStatusDto.FallenOff) {
      return "has fallen off"
    }
    if (status === PatchApplicationStatusDto.Removed) {
      return "has been removed"
    }
    if (status === PatchApplicationStatusDto.Reapplied) {
      return "has been reapplied"
    }
    return "is applied";
  }
  return (
      <>
        <StatusBox>
          <Text weight={'bold'}>Current Status as at {DateUtils.toDisplayString(patchApplication.clientUpdatedAt)}</Text>
          <ActivityBox>
            <Layout>
              
              <NameValue name="Status">
                <span  style={{ color: "#B040F9" }}>PATCH </span> {getDescription(patchApplication.status)}                
              </NameValue>
              <NameValue name="Time">
                {DateUtils.toDisplayString(patchApplication.lastAppliedAt)} - {DateUtils.toDisplayString(DateUtils.toDate(patchApplication.scheduledEnd!))}
              </NameValue>
              <NameValue name="Applied to">
                {patchApplication.patchLocation?.description}
              </NameValue>
              {
                  patchApplication.comment &&
                  <NameValue name="Notes">
                    {patchApplication.comment}
                  </NameValue>
              }
              {
                (patchApplication.status === PatchApplicationStatusDto.Applied ||  patchApplication.status === PatchApplicationStatusDto.Reapplied) && 
                <ActionDiv>
                    <>
                      { props.displayAllActivity && new Date() < DateUtils.toDate(patchApplication.scheduledEnd!) &&
                        <ActionBox>
                          <ActionButton onClick={() => openActivityDialog(PatchApplicationActivityTypeDto.Sighted, props.drug, false)}>SIGHT NOW</ActionButton>
                        </ActionBox>
                      } 
                      <ActionBox>
                        <ActionButton onClick={() => openActivityDialog(PatchApplicationActivityTypeDto.FallenOff, props.drug, false)}>REMOVE / FALLEN OFF</ActionButton>
                      </ActionBox>
                    </>
                </ActionDiv>
              }
              {
                  (patchApplication.status === PatchApplicationStatusDto.FallenOff) &&
                  <ActionDiv>
                    <>
                      { props.displayAllActivity && new Date() < DateUtils.toDate(patchApplication.scheduledEnd!) &&
                          <ActionBox>
                            <ActionButton onClick={() => openActivityDialog(PatchApplicationActivityTypeDto.Reapplied, props.drug, false)}>REAPPLY</ActionButton>
                          </ActionBox>
                      }
                    </>
                  </ActionDiv>
              }
           </Layout>
          </ActivityBox>
          
        </StatusBox>
        {
          // Only show ScheduledItemsBox if there are activities to display
          (() => {
            const filteredActivities = props.displayAllActivity ? activities?.filter(x => shouldBeDisplayed(x.scheduledActivity)) : getFirstScheduleItem(activities);
            const hasDisplayableActivities = filteredActivities?.some((activity, index) => props.displayAllActivity || index === 0);
            const hasReapplyCard = canManage(props.administrationPermissions, props.drug, false) && patchApplication.status === PatchApplicationStatusDto.FallenOff;
            

            if (!hasDisplayableActivities && !hasReapplyCard) {
                return null;
            }

            return (
                <ScheduledItemsBox>
                  <Layout gap={0.8}>
                    <Text weight={'bold'}>Activity</Text>
                    {
                      filteredActivities?.map((activity, index) => {
                        if (props.displayAllActivity || index === 0) {
                          return <PatchApplicationActivityCard
                              patchApplication={patchApplication} {...updatedProps(props, activity.scheduledActivity)}
                              {...props}
                              performedActivity={activity.performedActivity}
                              scheduledActivity={activity.scheduledActivity}
                              canBeSighted={!isAlreadyFinished && CheckIfCanBeSighted(activity.scheduledActivity)}
                              canBeSightedLate={!isAlreadyFinished && CheckIfCanBeSightedLate(activity.scheduledActivity)}
                              isFuture={isFuture(activity.scheduledActivity)}
                              key={index}
                              onOpen={
                                () => {
                                  openActivityDialog(activity.performedActivity?.activityType ?? (activity.scheduledActivity?.scheduledActivityType === PatchApplicationScheduledActivityTypeDto.Remove ? PatchApplicationActivityTypeDto.Removed : PatchApplicationActivityTypeDto.Sighted), 
                                      props.drug, !!activity.performedActivity, "Administer", activity.performedActivity, activity.scheduledActivity);
                                }
                              }
                              
                          ></PatchApplicationActivityCard>
                        }
                        return null;
                      })
                    }

                    {
                      // Display the resume.
                      hasReapplyCard &&
                      <PatchApplicationActivityCard
                          patchApplication={patchApplication}
                          {...props}
                          canBeSighted={false}
                          canBeSightedLate={false}
                          isFuture={false}
                          onOpen={() => {
                            openActivityDialog(PatchApplicationActivityTypeDto.Reapplied, props.drug, false, "Reapply");
                          }}
                          unscheduledActivity={{
                            activityType: PatchApplicationActivityTypeDto.Reapplied,
                            patchApplicationId: patchApplication.hsId,
                            patchApplicationClinicalSystemId: patchApplication.clinicalSystemId,
                          }}
                          key={-1}></PatchApplicationActivityCard>
                    }
                  </Layout>
                </ScheduledItemsBox>
            );
          })()
        }
        <PatchApplicationDialog
          patchApplication={patchApplication}
          patchApplicationActivity={patchApplicationActivity}
          patchApplicationScheduledActivity={patchApplicationScheduledActivity}
          open={open}
          closeDialog={() => setOpen(false)}
          {...props}
          label="OK"
          viewOnly={viewOnly}
          hideButton={true}
          activityType={activityKind}
      />              
    </>
  );
}
interface NameValueProps {
  name: string;
  children: React.ReactNode;
  bold?: boolean;
}
export function NameValue(props: NameValueProps) {
  let style= {};
  let bold = true;
  if (props.bold === undefined) {
    bold = true;
  }
  else if (props.bold === false) {
    bold = false;
  }
  if (bold) {
    style = { fontWeight: "bold"};
  }
  return <>
    <span>{props.name}</span>
    <ValueDiv>
      <span style={style}>{props.children}</span>
    </ValueDiv>
  </>
}
interface BatteryLevelProps {
  value: number;
}
function BatteryLevel(props: BatteryLevelProps) {
  let style= {};
  if (props.value <= 50) {
    style = {
      color: "red"
    };
  } 
  return (<span style={style}>{props.value}%</span>);
}
interface ConditionProps {
  value: string | undefined | null;
}
function SiteCondition(props: ConditionProps) {
  let style= {};
  if (!props.value) {
    return null;
  }
  const lcText = props.value.toLowerCase();
  if (lcText.includes("red") || lcText.includes("inflame") || lcText.includes("leak")) {
    style = {
      color: "red"
    };
  }
  return (<>&nbsp;-&nbsp;<span style={style}>{props.value}</span></>);
}
function LineCondition(props: ConditionProps) {
  let style= {};
  if (!props.value) {
    return null;
  }
  const lcText = props.value.toLowerCase();
  if (lcText.includes("cloudy")) {
    style = {
      color: "red"
    };
  }
  return (<span style={style}>{props.value}</span>);
}
interface PatchApplicationActivityCardProps extends Props {
  drug: HSDrug;
  patchApplication: PatchApplicationDto;
  performedActivity?: PatchApplicationActivityDto;
  scheduledActivity?: PatchApplicationScheduledActivityDto;
  unscheduledActivity?: PatchApplicationActivityDto;
  canBeSighted: boolean;
  canBeSightedLate: boolean;
  isLate: boolean;
  isFuture: boolean;
  hasBeenDosed: boolean;
  onOpen: () => void;
}

// eslint-disable-next-line sonarjs/cognitive-complexity, max-lines-per-function
function PatchApplicationActivityCard(props: PatchApplicationActivityCardProps) {
  function openActivityDialog(kind: PatchApplicationActivityTypeDto| undefined, sched: PatchApplicationScheduledActivityDto | undefined, perf: PatchApplicationActivityDto | undefined) {
    if (!canManage(props.administrationPermissions, props.drug, kind === PatchApplicationActivityTypeDto.Sighted)) {
      toasts.error('You do not have permission to manage this patch');
    } 
    else {
      props.onOpen();
    }
  }
  const activityType = props.performedActivity?.activityType ?? props.scheduledActivity?.scheduledActivityType;
  const activityTime = props.performedActivity?.clientCreatedAt ?? props.scheduledActivity?.scheduledAt;
  const userUtils = useApiUtils().users;

  function getActivityName(at: PatchApplicationActivityTypeDto | undefined) : string {
    if (!at) {
      return "";
    }
    switch(at) {
    case PatchApplicationActivityTypeDto.Applied:
      return 'APPLY';
    case PatchApplicationActivityTypeDto.Sighted:
      return 'SIGHT';
    case PatchApplicationActivityTypeDto.FallenOff:
      return 'FALLEN OFF';
    case PatchApplicationActivityTypeDto.Removed:
      return 'REMOVE';
    case PatchApplicationActivityTypeDto.Reapplied:
      return 'REAPPLY';
    }
    return "";
  }
  //eslint-disable-next-line sonarjs/cognitive-complexity
  return (
    <ActivityBox data-testid="PatchApplicationActivityCard">
      <Layout>
        <Text weight={'bold'}>
          {DateUtils.toDisplayString(DateUtils.toDate(activityTime!))}
          {!!props.scheduledActivity && !!props.performedActivity &&
              <i>&nbsp;(scheduled at {DateUtils.toDisplayString(DateUtils.toDate(props.scheduledActivity.scheduledAt!))})</i>
          }
        </Text>
        <Text>{getPatchApplicationDescription(userUtils, activityType, props.performedActivity?.clientUpdatedAt, props.performedActivity?.clientUser)}</Text>
      </Layout>
      {!!props.scheduledActivity && !props.performedActivity && (
          <>
            { !props.canBeSighted && !props.isFuture && <MissedBox> MISSED </MissedBox> }
            
            { props.canBeSighted && props.scheduledActivity.scheduledActivityType === PatchApplicationScheduledActivityTypeDto.Sight && props.scheduledActivity.status === PatchApplicationScheduledActivityStatusDto.Pending &&
                <ActionButton onClick={() => openActivityDialog(PatchApplicationActivityTypeDto.Sighted, props.scheduledActivity, props.performedActivity)}>SIGHT</ActionButton>
            }
            { (props.scheduledActivity.scheduledActivityType === PatchApplicationScheduledActivityTypeDto.Remove) &&
                <ActionButton onClick={() => openActivityDialog(PatchApplicationActivityTypeDto.Removed, props.scheduledActivity, props.performedActivity)}>REMOVE</ActionButton>
            }
          </>
      )}
      {!!props.performedActivity && (
            <ActionButton onClick={() => openActivityDialog(props.performedActivity?.activityType, props.scheduledActivity, props.performedActivity)}>VIEW</ActionButton>
      )}
      {!!props.unscheduledActivity && (
          <ActionButton onClick={() => openActivityDialog(props.performedActivity?.activityType, props.scheduledActivity, props.unscheduledActivity)}>{getActivityName(props.unscheduledActivity.activityType)}</ActionButton>
      )}
    </ActivityBox>
  );
}

const ActivityBox = styled.div`
  border-style: solid;
  border-color: #c5bacb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
`;

const ActionButton = styled(Button)`
  min-width: 10rem;
`;
const MissedBox = styled.div`
  min-width: 10rem;
  text-align: center;
`;

const ScheduledItemsBox = styled.div`
  background-color: rgba(246, 233, 254, 1);
  padding: 1.25rem;

  flex-direction: column;
  display: flex;
  width: 100%;
`;
const StatusBox = styled.div`
  background-color: rgba(246, 233, 254, 1);
  padding: 1.25rem;

  flex-direction: column;
  display: flex;
  width: 100%;
  
`;
const ActionDiv = styled.div`
  display: flex;
`
const ActionBox = styled.div`
  width: 15em;
  padding: 0.25rem;
`
const ValueDiv = styled.div`
  padding-bottom: 1em;
`;
